import { Add } from '@mui/icons-material';
import {
    Box,
    Button,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    type TextFieldProps,
    Typography,
} from '@mui/material';
import type { KeyboardEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import { _ } from '/@/locale';
import { setFarmingType } from '/@/store/carbon-calculator/actions';
import {
    getAllSoilTypesOptions,
    getCampaignFrom,
    getCampaignTo,
    getClay,
    getDensity,
    getFarmingOptions,
    getOrganicMaterial,
    getSelectedFarmingOption,
    getSoilType,
} from '/@/store/carbon-calculator/selectors';
import { type CropOption, INPUT_RANGES, type InputName, validateInput } from '../utils';
import { inputSx, sectionSx } from './styles';

interface Field {
    id: number;
    name: string;
    location: string;
}

interface Option {
    label: string;
    value: number | string;
}

interface FieldFormProps {
    fields: Field[];
    selectedFieldId: number;
    campaignsOptions: CropOption[];
    temperature: number | string;
    /** Set once the user tries to continue: from then on, empty required fields show an error. */
    showErrors: boolean;
    onZoneSelection: (fieldId: number) => void;
    onCreateField: () => void;
    onTempChange: (value: string) => void;
    onCampaignFromChange: (value: number) => void;
    onCampaignToChange: (value: number) => void;
    onSoilTypeChange: (value: string) => void;
    onClayChange: (value: string) => void;
    onOrganicMaterialChange: (value: string) => void;
    onDensityChange: (value: string) => void;
    onLoadMore: () => void;
}

export const OTHER_SOIL_LABELS = ['Otros', 'Outros', 'Other'];

export function SelectField<T extends number | string>({
    options,
    value,
    onChange,
    ...props
}: { options: { label: string; value: T }[]; value: T | undefined; onChange: (value: T) => void } & Omit<
    TextFieldProps,
    'onChange' | 'value'
>) {
    return (
        <TextField
            select
            fullWidth
            size='small'
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value as T)}
            sx={inputSx}
            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
            {...props}
        >
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
}

/** A number input validated against its `INPUT_RANGES` entry. */
function NumberField({
    name,
    value,
    showErrors,
    onChange,
    unit,
    ...props
}: {
    name: InputName;
    value: unknown;
    showErrors: boolean;
    onChange: (value: string) => void;
    unit?: string;
} & Omit<TextFieldProps, 'onChange' | 'value' | 'name'>) {
    const { min, max } = INPUT_RANGES[name];
    const error = validateInput(name, value);
    // An out-of-range value is flagged right away; an empty one only after trying to continue.
    const message =
        error === 'range'
            ? _('Ingresá un valor mayor a {{min}} y hasta {{max}}.', { min, max })
            : error === 'required' && showErrors
              ? _('Este campo es requerido.')
              : undefined;

    return (
        <TextField
            type='number'
            fullWidth
            size='small'
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value)}
            error={!!message}
            helperText={message}
            sx={inputSx}
            inputProps={{ min, max, step: 'any' }}
            InputProps={unit ? { endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> } : undefined}
            {...props}
        />
    );
}

export default function FieldForm({
    fields,
    selectedFieldId,
    campaignsOptions,
    temperature,
    showErrors,
    onZoneSelection,
    onCreateField,
    onTempChange,
    onCampaignFromChange,
    onCampaignToChange,
    onSoilTypeChange,
    onClayChange,
    onOrganicMaterialChange,
    onDensityChange,
    onLoadMore,
}: FieldFormProps) {
    const dispatch = useDispatch();
    const campaignFrom = useSelector(getCampaignFrom);
    const campaignTo = useSelector(getCampaignTo);
    const farmingOptions: Option[] = useSelector(getFarmingOptions);
    const farmingType = useSelector(getSelectedFarmingOption);
    const allSoilTypes: Option[] = useSelector(getAllSoilTypesOptions);
    const soilType = useSelector(getSoilType);
    const clay = useSelector(getClay);
    const organicMaterial = useSelector(getOrganicMaterial);
    const density = useSelector(getDensity);

    const requiredMessage = _('Este campo es requerido.');
    const requiredError = (value: unknown) =>
        showErrors && (value === undefined || value === '') ? requiredMessage : undefined;
    const isOtherSoil = OTHER_SOIL_LABELS.includes(soilType?.label);

    const handleOnKeyPress = (e: KeyboardEvent) => {
        if (e.key === 'Enter') onLoadMore();
    };

    return (
        <Stack spacing={3} sx={sectionSx}>
            <Typography variant='h6' color='primary'>
                {_('Cargá la franja de campañas que quieras. Podés elegir campañas anteriores o futuras.')}
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                <Stack spacing={0.5} flex={2} alignItems='flex-start' width='100%'>
                    <SelectField
                        label={_('Lote')}
                        value={selectedFieldId}
                        options={fields.map(({ name, location, id }) => ({
                            label: `${name} - ${location}`,
                            value: id,
                        }))}
                        onChange={onZoneSelection}
                    />
                    <Button color='secondary' size='small' startIcon={<Add />} onClick={onCreateField}>
                        {_('Creá un lote')}
                    </Button>
                </Stack>
                <Box flex={1} width='100%'>
                    <NumberField
                        name='temperature'
                        label={_('Temperatura promedio histórico')}
                        unit='°C'
                        value={temperature}
                        showErrors={showErrors}
                        onChange={onTempChange}
                    />
                </Box>
            </Stack>

            <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={2}>
                <SelectField
                    label={_('Desde campaña')}
                    value={campaignFrom}
                    options={campaignsOptions.slice(0, -1)}
                    onChange={onCampaignFromChange}
                    error={!!requiredError(campaignFrom)}
                    helperText={requiredError(campaignFrom)}
                />
                <SelectField
                    label={_('Hasta campaña')}
                    value={campaignTo}
                    options={campaignsOptions.filter((el) => el.value > campaignFrom).slice(0, 10)}
                    onChange={onCampaignToChange}
                    error={!!requiredError(campaignTo)}
                    helperText={requiredError(campaignTo)}
                />
                <SelectField
                    label={_('Tipo de labranza')}
                    value={farmingType}
                    options={farmingOptions}
                    onChange={(value) => dispatch(setFarmingType(value))}
                    error={!!requiredError(farmingType)}
                    helperText={requiredError(farmingType)}
                />
                <SelectField
                    label={_('Tipo de suelo')}
                    value={soilType?.label}
                    options={(allSoilTypes || []).map(({ label }) => ({ label, value: label }))}
                    onChange={onSoilTypeChange}
                    error={!!requiredError(soilType)}
                    helperText={requiredError(soilType)}
                />
            </Box>

            {isOtherSoil && (
                <Alert severity='blue'>
                    {_('Eligiendo "Otros" en tipo de suelo, deberán cargarse los datos de abajo manualmente.')}
                </Alert>
            )}

            {soilType && farmingType !== undefined && (
                <Stack spacing={2}>
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {_(
                            'Los siguientes datos son estimados por su serie de suelos. Para mayor precisión complete con sus propios datos.'
                        )}
                        {' (*)'}
                    </Typography>
                    <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, 1fr)' }} gap={2}>
                        <NumberField
                            name='clay'
                            label={_('Arcilla en el suelo (%)')}
                            value={clay === undefined ? soilType.clay : clay}
                            showErrors={showErrors}
                            onChange={onClayChange}
                            onKeyDown={handleOnKeyPress}
                        />
                        <NumberField
                            name='organicMaterial'
                            label={_('% de Materia orgánica')}
                            value={organicMaterial === undefined ? soilType.organicMaterial : organicMaterial}
                            showErrors={showErrors}
                            onChange={onOrganicMaterialChange}
                            onKeyDown={handleOnKeyPress}
                        />
                        <NumberField
                            name='density'
                            label={_('Densidad aparente (gr/cm3)')}
                            value={density === undefined ? soilType.apparentDensity : density}
                            showErrors={showErrors}
                            onChange={onDensityChange}
                            onKeyDown={handleOnKeyPress}
                        />
                    </Box>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent='space-between'
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {'(*) '}
                            {_('Estas mediciones corresponden a extracto de suelo de 0-20cm.')}
                        </Typography>
                        <Button variant='contained' color='secondary' onClick={onLoadMore}>
                            {_('Cargá tus cultivos')}
                        </Button>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}
